import {Box, createStyles, Divider, IconButton, makeStyles, Theme, Tooltip} from '@material-ui/core';
import {Delete, Edit, Warning} from '@material-ui/icons';
import {Prescription, PrescriptionVerificationOrderResult, PrescriptionVerificationResult} from '../../../types/prescription';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import { isPrescriptionFilled } from '../../../utils/prescription';
import ErrorIcon from '../../common/icons/ErrorIcon/ErrorIcon';
import WarningIcon from '../../common/icons/WarningIcon/WarningIcon';
import { filterPrescriptionVerificationResults } from '../../../utils/p1';
import {getDosageDisplayValue} from '../../../utils/prescriptionDosage';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        iconWithTooltip: {
            display: 'inline-flex', 
            alignItems: 'center', 
            marginBottom: 0
        },
        prescriptionItemHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
        },
        prescriptionItemHeaderText: {
            margin: '0 0 10px 0', 
            display: 'flex', 
            alignItems: 'center'
        },
        [theme.breakpoints.down('sm')]: {
            iconWithTooltip: {
                marginBottom: 3,
            }
        }
    })
);

export interface SelectedPrescription extends Prescription {
    id: string;
}

type PrescriptionItemProps = {
    prescription: SelectedPrescription;
    onDelete: (prescriptionId: string) => void;
    index: number;
    onEdit?: (prescriptionId: string) => void;
    verificationResults?: PrescriptionVerificationResult[];
    isInEdit?: boolean;
};

const IconWithTooltip = ({icon, tooltip}: {icon: React.ReactNode, tooltip: string}) => {
    const classes = useStyles();
    return (
        <Tooltip title={tooltip}>
            <div className={classes.iconWithTooltip}>
                {icon}
            </div>
        </Tooltip>
    );
}

const PrescriptionItem = ({
    prescription,
    onDelete,
    index,
    onEdit,
    verificationResults,
    isInEdit
}: PrescriptionItemProps) => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {errors, warnings} = filterPrescriptionVerificationResults(verificationResults ?? []);
    const classes = useStyles();

    const renderIcons = () => (
        <>
            {errors?.map((error, index) => (
                <IconWithTooltip 
                    key={index} 
                    icon={<ErrorIcon style={{marginLeft: 16}} />} 
                    tooltip={error.description} 
                />
            ))}
            {warnings?.map((warning, index) => (
                <IconWithTooltip 
                    key={index} 
                    icon={<WarningIcon style={{marginLeft: 16}} />} 
                    tooltip={warning.description} 
                />
            ))}
        </>
    );

    return (
        <Box style={{marginBottom: 20}}>
            <Box
                className={classes.prescriptionItemHeader}
            >
                <h2 className={classes.prescriptionItemHeaderText}>
                    Lek {index + 1}
                    {!isPrescriptionFilled(prescription) ? (
                        <IconWithTooltip 
                            icon={<ErrorIcon style={{marginLeft: 16}} />} 
                            tooltip={t('fillPrescriptionRequired')} 
                        />
                    ) : 
                        renderIcons()
                    }
                </h2>
                <Box>
                    {onEdit && (
                        <IconButton onClick={() => onEdit(prescription.id)} disabled={isInEdit}>
                            <Edit />
                        </IconButton>
                    )}
                    <IconButton onClick={() => onDelete(prescription.id)} disabled={isInEdit}>
                        <Delete />
                    </IconButton>
                </Box>
            </Box>
            <Box>{prescription.medicinalProduct.displayValue}</Box>
            <Box>
                {prescription.quantity} {t('packs')}
                {prescription.medicationPack.displayValue && `, ${prescription.medicationPack.displayValue}`}
            </Box>
            <Box>{getDosageDisplayValue(prescription, t)}</Box>
            {prescription.reimbursement && (
                <Box>
                    {t('paymentRate')}:{' '}
                    {prescription.reimbursement?.paymentLevelDisplayValue}
                </Box>
            )}
            <Divider style={{marginTop: 20}} />
        </Box>
    );
};
export default PrescriptionItem;
